/**
 * Where an integration config field is saved (CC 0516).
 *
 * `PATCH /v1/admin/integrations/{provider}/config` only accepts the providers in
 * `INTEGRATION_CONFIG_EDITABLE_PROVIDER_IDS`; for any other provider it answers
 * 400 INTEGRATION_CONFIG_UNSUPPORTED. A field that belongs to a FEATURE of such a
 * provider (google's gmail_send `sender_address`) lives in the feature-settings
 * store and is saved with `PUT …/features/{feature}/settings/{fieldKey}`, one
 * request per field. Editable providers (twilio, …) keep PATCH /config exactly as
 * before, feature or not.
 */
import { INTEGRATION_CONFIG_EDITABLE_PROVIDER_IDS } from "@v0y4ge/api-contracts";

export function configEditableProvider(provider: string | null | undefined): boolean {
  const p = String(provider ?? "").trim().toLowerCase();
  return (INTEGRATION_CONFIG_EDITABLE_PROVIDER_IDS as readonly string[]).includes(p);
}

/** True when this field saves through the feature-settings route rather than PATCH /config. */
export function savesAsFeatureSetting(provider: string | null | undefined, feature: string | null | undefined): boolean {
  return !!String(feature ?? "").trim() && !configEditableProvider(provider);
}

export interface ConfigSaveDraft { key: string; value: string; feature?: string }
export interface ConfigSaveOps {
  patchConfig: (provider: string, body: { config: Record<string, string> }) => Promise<unknown>;
  putSetting: (provider: string, feature: string, fieldKey: string, value: string) => Promise<unknown>;
}

/** The server's own words for a refusal (e.g. 400 INVALID_SETTING_VALUE's message). */
export function refusalMessage(e: unknown): string {
  const m = e instanceof Error ? e.message : typeof e === "string" ? e : "";
  return m.trim() || "This value was refused.";
}

/**
 * Save a set of drafts. Feature-setting drafts are PUT one at a time, trimmed; each refusal is
 * kept against its field and the rest still go. The remaining drafts go in ONE PATCH /config, as
 * before; a refusal there is thrown to the caller, which already maps it onto fields.
 * Answers which keys were saved and the refused feature-setting fields with the server's message.
 */
export async function saveConfigDrafts(provider: string, drafts: ConfigSaveDraft[], ops: ConfigSaveOps): Promise<{ saved: string[]; refused: Record<string, string> }> {
  const saved: string[] = [];
  const refused: Record<string, string> = {};
  const config: Record<string, string> = {};
  for (const d of drafts) {
    if (d.feature && savesAsFeatureSetting(provider, d.feature)) {
      try { await ops.putSetting(provider, d.feature, d.key, d.value.trim()); saved.push(d.key); }
      catch (e) { refused[d.key] = refusalMessage(e); }
    } else {
      config[d.key] = d.value;
    }
  }
  const keys = Object.keys(config);
  if (keys.length) { await ops.patchConfig(provider, { config }); saved.push(...keys); }
  return { saved, refused };
}
